// File: frontend/src/services/knowledgeGraphApi.ts
import {
  KGStatsResponse,
  KGSearchResponse,
  KGClusterResponse,
  KGTimelineResponse,
  KGAttackPathResponse,
  KGCrossReportResponse,
} from '../pages/KnowledgeGraph/types';

// --- Mock Data ---

const MOCK_DELAY = 500;

const mockStats: KGStatsResponse = {
  reports: 929,
  canon_entities: 21601,
  observed_rel: 59783,
  inferred_rel_all: 46,
  inferred_rel_high_conf: 12,
  top_entities: [
    { id: 'canon-ps', name: 'PowerShell', type: 'Tool', report_count: 72 },
    { id: 'canon-ph', name: 'Phishing and Remote Access Tool Abuse', type: 'AttackPattern', report_count: 33 },
  ],
  type_distribution: {
    AttackPattern: 1060,
    Tool: 882,
    Indicator: 822,
    Malware: 517,
    ThreatActor: 360,
    Identity: 257,
  },
};

const mockSearchResults: KGSearchResponse = {
  count: 3,
  results: [
    {
      id: 'canon-forest-blizzard',
      name: 'Forest Blizzard',
      type: 'ThreatActor',
      aliases: ['APT28', 'Fancy Bear', 'Sofacy', 'Pawn Storm', 'STRONTIUM'],
      external_ids: ['G0007'],
      report_count: 4,
    },
    {
      id: 'canon-bitter-apt',
      name: 'BITTER APT',
      type: 'ThreatActor',
      aliases: ['T-APT-17'],
      report_count: 2,
    },
    {
      id: 'canon-dprk',
      name: 'DPRK',
      type: 'ThreatActor',
      aliases: ['Lazarus Group', 'APT38'],
      report_count: 5,
    },
  ],
};

const mockCluster: KGClusterResponse = {
  node_count: 8,
  edge_count: 7,
  nodes: [
    { id: 'canon-forest-blizzard', name: 'Forest Blizzard', type: 'ThreatActor', report_count: 4 },
    { id: 'canon-mimikatz', name: 'Mimikatz', type: 'Tool', report_count: 12 },
    { id: 'canon-cobalt-strike', name: 'Cobalt Strike', type: 'Tool', report_count: 18 },
    { id: 'canon-prismex', name: 'PRISMEX', type: 'Malware', report_count: 2 },
    { id: 'canon-ps', name: 'PowerShell', type: 'Tool', report_count: 72 },
    { id: 'canon-cred-harvest', name: 'Credential Harvesting', type: 'AttackPattern', report_count: 8 },
    { id: 'canon-spearphishing', name: 'Spearphishing', type: 'AttackPattern', report_count: 15 },
    { id: 'canon-lateral-movement', name: 'Lateral Movement', type: 'AttackPattern', report_count: 6 },
  ],
  edges: [
    { source: 'canon-forest-blizzard', target: 'canon-mimikatz', type: 'uses', edge_kind: 'OBSERVED_REL', mention_count: 3 },
    { source: 'canon-forest-blizzard', target: 'canon-cobalt-strike', type: 'uses', edge_kind: 'OBSERVED_REL', mention_count: 5 },
    { source: 'canon-forest-blizzard', target: 'canon-prismex', type: 'uses', edge_kind: 'OBSERVED_REL', mention_count: 2 },
    { source: 'canon-forest-blizzard', target: 'canon-ps', type: 'uses', edge_kind: 'OBSERVED_REL', mention_count: 4 },
    { source: 'canon-forest-blizzard', target: 'canon-cred-harvest', type: 'employs', edge_kind: 'OBSERVED_REL', mention_count: 3 },
    { source: 'canon-forest-blizzard', target: 'canon-spearphishing', type: 'employs', edge_kind: 'OBSERVED_REL', mention_count: 6 },
    { source: 'canon-forest-blizzard', target: 'canon-lateral-movement', type: 'employs', edge_kind: 'OBSERVED_REL', mention_count: 2 },
  ],
};

const mockTimeline: KGTimelineResponse = {
  name: 'Forest Blizzard',
  type: 'ThreatActor',
  aliases: ['APT28', 'Fancy Bear', 'Sofacy', 'Pawn Storm', 'STRONTIUM'],
  total_mentions: 4,
  appearances: [
    {
      report_id: 'rpt-001',
      title: 'Forest Blizzard Targets Ukrainian Defense Sector with New PRISMEX Backdoor',
      date: '2026-04-18',
      source: 'Microsoft Threat Intelligence',
      surface_form: 'Forest Blizzard',
    },
    {
      report_id: 'rpt-002',
      title: 'APT28 Leverages Zero-Day in Government Networks',
      date: '2026-04-05',
      source: 'Mandiant',
      surface_form: 'APT28',
    },
    {
      report_id: 'rpt-003',
      title: 'Fancy Bear Spearphishing Campaign Hits NATO Allies',
      date: '2026-03-22',
      source: 'CrowdStrike',
      surface_form: 'Fancy Bear',
    },
    {
      report_id: 'rpt-004',
      title: 'STRONTIUM Activity Cluster Analysis Q1 2026',
      date: '2026-03-10',
      source: 'Recorded Future',
      surface_form: 'STRONTIUM',
    },
  ],
};

const mockAttackPath: KGAttackPathResponse = {
  count: 5,
  results: [
    { id: 'canon-forest-blizzard', name: 'Forest Blizzard', type: 'ThreatActor', report_count: 4 },
    { id: 'canon-lazarus', name: 'Lazarus Group', type: 'ThreatActor', report_count: 5 },
    { id: 'canon-cobalt-strike', name: 'Cobalt Strike', type: 'Tool', report_count: 18 },
    { id: 'canon-emotet', name: 'Emotet', type: 'Malware', report_count: 7 },
    { id: 'canon-ps', name: 'PowerShell', type: 'Tool', report_count: 72 },
  ],
};

const mockCrossReport: KGCrossReportResponse = {
  count: 3,
  shared_entities: [
    { id: 'canon-cobalt-strike', name: 'Cobalt Strike', type: 'Tool' },
    { id: 'canon-spearphishing', name: 'Spearphishing', type: 'AttackPattern' },
    { id: 'canon-ps', name: 'PowerShell', type: 'Tool' },
  ],
};

// --- Helper ---

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function warnMock(endpoint: string) {
  console.warn(`KG API not available, using mock data (${endpoint})`);
}

// The FastAPI /cluster endpoint serializes edges as
// { label, edge_kind: 'observed'|'inferred', report_ids } while the UI works
// with { type, edge_kind: 'OBSERVED_REL'|'INFERRED_REL', mention_count }.
// Normalize here so both shapes (and the mocks) render identically.
function normalizeCluster(data: any): KGClusterResponse {
  const edges = (data.edges || []).map((e: any) => ({
    source: e.source,
    target: e.target,
    type: e.type ?? e.label ?? 'related',
    edge_kind:
      e.edge_kind === 'observed' ? 'OBSERVED_REL'
      : e.edge_kind === 'inferred' ? 'INFERRED_REL'
      : e.edge_kind,
    confidence: e.confidence ?? 0,
    mention_count: e.mention_count ?? (Array.isArray(e.report_ids) ? e.report_ids.length : 1),
  }));
  return { ...data, edges };
}

// --- Service Factory ---

export function createKnowledgeGraphApi(authenticatedFetcher: (endpoint: string) => Promise<any>) {

  async function tryFetchOrMock<T>(endpoint: string, mockData: T): Promise<T> {
    try {
      return await authenticatedFetcher(endpoint);
    } catch {
      warnMock(endpoint);
      await delay(MOCK_DELAY);
      return mockData;
    }
  }

  return {
    getStats(): Promise<KGStatsResponse> {
      return tryFetchOrMock('/api/kg/stats', mockStats);
    },

    search(query: string, entityType?: string, limit = 20): Promise<KGSearchResponse> {
      const params = new URLSearchParams({ query, limit: String(limit) });
      if (entityType) params.set('entity_type', entityType);
      return tryFetchOrMock(`/api/kg/search?${params}`, mockSearchResults);
    },

    getCluster(canonId: string, depth = 1, includeInferred = false, minConfidence = 0.7): Promise<KGClusterResponse> {
      const params = new URLSearchParams({
        depth: String(depth),
        include_inferred: String(includeInferred),
        min_inferred_confidence: String(minConfidence),
      });
      return tryFetchOrMock(`/api/kg/cluster/${encodeURIComponent(canonId)}?${params}`, mockCluster)
        .then(normalizeCluster);
    },

    getTimeline(canonId: string): Promise<KGTimelineResponse> {
      return tryFetchOrMock(`/api/kg/timeline/${encodeURIComponent(canonId)}`, mockTimeline);
    },

    getAttackPath(ttpQuery: string, depth = 2): Promise<KGAttackPathResponse> {
      const params = new URLSearchParams({ ttp_query: ttpQuery, depth: String(depth) });
      return tryFetchOrMock(`/api/kg/attack-path?${params}`, mockAttackPath);
    },

    getCrossReport(reportIdA: string, reportIdB: string): Promise<KGCrossReportResponse> {
      const params = new URLSearchParams({ report_id_a: reportIdA, report_id_b: reportIdB });
      return tryFetchOrMock(`/api/kg/cross-report?${params}`, mockCrossReport);
    },
  };
}

export type KnowledgeGraphApi = ReturnType<typeof createKnowledgeGraphApi>;
